import { type ReactElement, type ReactNode } from 'react';
import { Popover } from 'react-tiny-popover';
import { useEscapeToClose } from '~/hooks/useEscapeToClose';
import { EditCellOverlay } from './EditCellOverlay';

export interface PopoverCellEditorProps {
  isOpen: boolean;
  onDone: () => void;
  editor: ReactNode;
  anchor: ReactElement;
  canClose?: boolean;
}

/**
 * Floats a cell's own editor beside the cell through a portal.
 *
 * The table body scrolls horizontally, and `overflow-x: auto` makes the browser
 * compute `overflow-y: auto` too, so the scrollport clips anything positioned
 * inside it — a tall editor cannot escape by being `position: absolute`. The
 * portal takes it out of the scrollport entirely.
 */
export function PopoverCellEditor({
  isOpen,
  onDone,
  editor,
  anchor,
  canClose = true,
}: Readonly<PopoverCellEditorProps>) {
  useEscapeToClose({ isOpen, onClose: onDone, canClose });

  return (
    <Popover
      isOpen={isOpen}
      positions={['bottom', 'top']}
      align="end"
      padding={4}
      containerStyle={{ zIndex: '1100' }}
      // Capture, so an outside press closes this editor before the pencil it
      // landed on opens the next one; on bubble the order reverses and the
      // close wins.
      clickOutsideCapture
      onClickOutside={canClose ? onDone : () => {}}
      content={<EditCellOverlay position="anchored">{editor}</EditCellOverlay>}
    >
      <span className="inline-flex">{anchor}</span>
    </Popover>
  );
}
